import { useGroupLifeQuote } from '@/features/quotes/context/group-life-quote.context';
import { useQuoteParams } from '@/features/quotes/context/quote-params.context';
import {
    mapHydratedPlan,
    shouldApplyHydratedPlan,
} from '@/features/quotes/utils/group-life/plan-hydration';
import { groupLifeService } from '@/services';
import { cleanCnpjOrCpf } from '@/shared/utils/document.util';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { useQuotationVersionId } from './use-quotation-version-id.hook';

/**
 * Quando o usuário recarrega a página do wizard Group Life (URL contém
 * `policyId`), o contexto in-memory (`useGroupLifeQuote`) perde tudo —
 * este hook re-hidrata o contexto buscando o snapshot completo da cotação
 * no InsureMO via `GET /portal/v1/group-life/quotes/:policyId`.
 *
 * Vidas (members) ainda não vêm — endpoints de PolicyRisk continuam
 * bloqueados no InsureMO. Stipulator/Plan/SubStipulators/Commercialization
 * são restaurados.
 */
export function useHydrateGroupLifeQuote() {
  const queryClient = useQueryClient();
  const search = useSearch({ strict: false }) as { quoteFlow?: string };
  const isEditFlow = search.quoteFlow === 'edit';
  const versionId = useQuotationVersionId();
  const { policyId, productCode, setParams } = useQuoteParams();
  const {
    stipulator,
    plan,
    commercialization,
    setStipulator,
    setPlan,
    setCommercialization,
    addSubStipulator,
    subStipulators,
    clearAllMembers,
  } = useGroupLifeQuote();

  const enabled = !!policyId;
  void productCode;

  const hydratedForRef = useRef<string | null>(null);

  const detailsQueryKey = versionId
    ? ['group-life', 'quote-at-version', policyId, versionId]
    : ['group-life', 'quote-details', policyId];

  // Sempre busca snapshot fresco ao abrir/reabrir a cotação (sem cache).
  useEffect(() => {
    hydratedForRef.current = null;
    if (!policyId) return;
    void queryClient.invalidateQueries({ queryKey: detailsQueryKey });
    // detailsQueryKey deriva de policyId+versionId (deps abaixo cobrem os dois).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policyId, versionId, queryClient]);

  const query = useQuery({
    queryKey: detailsQueryKey,
    queryFn: async () => {
      if (!policyId) throw new Error('policyId é obrigatório');
      const result = versionId
        ? await groupLifeService.getQuoteAtVersion(policyId, versionId)
        : await groupLifeService.getQuoteDetails(policyId);
      if (!result.success || !result.data) {
        throw new Error(
          result.error ?? 'Falha ao carregar a cotação Group Life',
        );
      }
      return result.data;
    },
    enabled,
    // Snapshot sempre fresco ao abrir/reabrir a cotação (semântica de edição).
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    // `false`: num wizard de EDIÇÃO, refetchar o snapshot inteiro toda vez que a
    // aba recebe foco é desperdício de latência/banda (o snapshot pode ter 5k+
    // vidas) e não agrega — o dado só precisa vir fresco ao abrir. Antes era
    // `true`, causando refetch do snapshot completo a cada foco de janela.
    refetchOnWindowFocus: false,
    retry: false,
  });

  useEffect(() => {
    if (!query.data) return;
    // Inclui a versão na chave do guard: versões diferentes compartilham o
    // mesmo policyId, então sem isso a troca de versão não re-hidrataria.
    const hydrationKey = `${query.data.policyId}:${versionId ?? ''}`;
    if (hydratedForRef.current === hydrationKey) return;
    hydratedForRef.current = hydrationKey;

    const d = query.data;

    if (d.proposalNumber) {
      setParams({ proposalNumber: d.proposalNumber });
    }

    // Estipulante: o back é autoritativo para o DOCUMENTO (cura o CNPJ que o
    // rascunho local possa ter guardado mascarado/ofuscado) e para a Razão
    // Social. Os demais campos (atividade/contato) só são preenchidos quando
    // o back tem valor — caso contrário preserva o que o rascunho local trouxe.
    if (d.stipulator) {
      // CNPJ pode ser alfanumérico (RFB): normaliza preservando letras.
      const backendDoc = cleanCnpjOrCpf(d.stipulator.registerNumber);
      const useBackendDoc = backendDoc.length >= 11; // documento limpo válido
      setStipulator({
        insuranceType:
          d.stipulator.insuranceType ?? stipulator?.insuranceType ?? '1',
        isCaepf: d.stipulator.isCaepf,
        registerNumber: useBackendDoc
          ? d.stipulator.registerNumber
          : (stipulator?.registerNumber ?? d.stipulator.registerNumber),
        responsibleName:
          d.stipulator.responsibleName || stipulator?.responsibleName || '',
        // `||` (não `??`): o back devolve string vazia quando não persiste o
        // campo — nesse caso preservamos o valor do rascunho local.
        cnaeCode: d.stipulator.cnaeCode || stipulator?.cnaeCode || '',
        email: d.stipulator.email || stipulator?.email || '',
        phone: d.stipulator.phone || stipulator?.phone || '',
        // Congênere (Transferência) — lido de policy.SeguradoraAnterior.
        insurerCompanyId:
          d.stipulator.transferOriginInsurerCode ||
          stipulator?.insurerCompanyId ||
          undefined,
        // Idades médias da Diretrix (PolicyCustomer.AveragePartnerAge/
        // AverageEmployeeAge).
        averagePartnerAge:
          d.stipulator.averagePartnerAge ?? stipulator?.averagePartnerAge,
        averageEmployeeAge:
          d.stipulator.averageEmployeeAge ?? stipulator?.averageEmployeeAge,
      });
    }

    // Plano: o InsureMO é autoritativo quando o step 2 já foi salvo. O
    // localStorage pode trazer um plano parcial (só `coverages` via
    // `updateCoverage`) — nesse caso o GET deve prevalecer.
    if (d.plan && (isEditFlow || shouldApplyHydratedPlan(plan, d.plan))) {
      setPlan(mapHydratedPlan(d.plan));
    }

    if (d.commercialization && (isEditFlow || !commercialization)) {
      setCommercialization({
        // O contexto guarda string sempre; os campos são opcionais na origem
        // (agencyPercent é alias legado de agencingPercent, nunca vêm os dois).
        brokeragePercent: d.commercialization.brokeragePercent ?? '',
        agencingPercent: d.commercialization.agencingPercent ?? '',
        agencyPercent: d.commercialization.agencyPercent ?? '',
        surchargePercent: d.commercialization.surchargePercent,
        actuarialDiscountPercent: d.commercialization.actuarialDiscountPercent,
        leaderSharePercent: d.commercialization.leaderSharePercent ?? '',
        coBrokers: d.commercialization.coBrokers ?? [],
      });
    }

    // SubStipulators: adiciona cada um PRESERVANDO o id do back (=
    // PolicyElementId do PolicyCustomer). Sem isso, o front gera uma uuid
    // local e perde o link com `subStipulatorId` das vidas — o sub fica com
    // grid vazio.
    if (d.subStipulators.length > 0 && subStipulators.length === 0) {
      for (const sub of d.subStipulators) {
        addSubStipulator({
          id: sub.id,
          cnpj: sub.cnpj,
          legalName: sub.legalName,
          cnaeCode: sub.cnaeCode,
          email: sub.email,
          phone: sub.phone,
        });
      }
    }

    // VIDAS: NÃO são mais carregadas aqui. Cada MemberGroupCard (Step 3) faz
    // sua própria paginação server-side via `useMembersPage`
    // (GET /quotes/:id/members/page) — escala para 5000+ vidas sem trazer o
    // conjunto inteiro para o contexto. Limpamos os arrays de vidas do
    // contexto (que deixaram de ser a fonte do grid) por consistência.
    clearAllMembers();
  }, [
    query.data,
    stipulator,
    plan,
    commercialization,
    setStipulator,
    setPlan,
    setCommercialization,
    addSubStipulator,
    subStipulators.length,
    clearAllMembers,
    setParams,
    isEditFlow,
    versionId,
  ]);

  return {
    // `isLoading` cobre a carga inicial do snapshot. O `GroupLifeHydrator`
    // permanece montado ao navegar entre steps, então a query não refaz e o
    // gate de skeleton só aparece na abertura da edição, não a cada step.
    isHydrating: query.isLoading,
    isEditFlow,
    hydrationError: query.error,
  };
}
